/**
 * `/announcements` — the permanent archive. Unlike the header notification bell,
 * which only ever lists the newest 5, this shows every notification — active or
 * archived — newest first. See docs/announcements-maintenance.md.
 */

import { Link } from 'react-router-dom'
import { Layout } from '@/components/layout/Layout'
import { NOTIFICATION_VISUALS } from '@/components/layout/notificationVisuals'
import { useSeo } from '@/seo/useSeo'
import { SITE } from '@/config/site'
import { getAllAnnouncements } from '@/lib/notifications'
import { formatIstDate, parseIstDateTime } from '@/lib/istDate'

export const AnnouncementsPage = () => {
  useSeo()
  const announcements = getAllAnnouncements()

  return (
    <Layout>
      <section className="py-6 sm:py-10">
        <h1 className="text-balance text-4xl font-bold tracking-tight sm:text-5xl">Announcements</h1>
        <p className="mt-3 max-w-2xl opacity-80">
          Important updates, maintenance notices, and useful information from {SITE.name}.
        </p>

        {announcements.length === 0 ? (
          <p className="mt-10 opacity-70">No announcements yet — check back soon.</p>
        ) : (
          <ol className="mt-8 divide-y divide-base-300 border-y border-base-300">
            {announcements.map((n) => {
              const { icon: Icon, className } = NOTIFICATION_VISUALS[n.type]
              const ms = parseIstDateTime(n.date)
              return (
                <li key={n.id} className="py-5">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <Icon size={16} className={className} aria-hidden="true" />
                    <span className="font-semibold">{n.title}</span>
                    {!n.active && <span className="badge badge-ghost badge-sm">Archived</span>}
                  </div>
                  {ms !== null && (
                    <p className="mt-1 text-xs opacity-60">
                      <time dateTime={new Date(ms).toISOString()}>{formatIstDate(ms)}</time>
                    </p>
                  )}
                  <p className="mt-2 opacity-85">{n.message}</p>
                  {n.link && (
                    <Link to={n.link} className="link link-primary mt-2 inline-block text-sm font-medium">
                      Read more →
                    </Link>
                  )}
                </li>
              )
            })}
          </ol>
        )}

        <div className="mt-12 rounded-box border border-base-300 bg-base-100 p-5 text-center sm:p-6">
          <h2 className="font-semibold">Have something useful to share?</h2>
          <p className="mx-auto mt-1 max-w-md text-sm opacity-70">
            If you know about an important ATS, resume, job-search, or career-related update that
            could help others, contact us. We review submissions and share only information that is
            useful and relevant.
          </p>
          <Link to="/contact" className="btn btn-outline btn-sm mt-4">
            Contact Us →
          </Link>
        </div>
      </section>
    </Layout>
  )
}

export default AnnouncementsPage
